import React, { useState, useRef, useEffect, useCallback } from 'react';
import SafeIcon from '../common/SafeIcon';

const PREVIEW_DURATION = 10;

// Global audio manager — singleton to prevent multiple audios playing
let globalAudio = null;
let globalAudioId = null;

const stopGlobalAudio = () => {
  if (globalAudio) {
    globalAudio.pause();
    globalAudio.currentTime = 0;
    globalAudio = null;
    globalAudioId = null;
  }
};

const MusicPlayer = ({ audioUrl, title, onSelect, isSelected, isPremium, emoji, artist, trackId }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const timerRef = useRef(null);
  const timeoutRef = useRef(null);
  const idRef = useRef(trackId || title);

  const stopAudio = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (globalAudioId === idRef.current) {
      stopGlobalAudio();
    }
    setPlaying(false);
    setProgress(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (globalAudioId === idRef.current) {
        stopGlobalAudio();
      }
    };
  }, []);

  // Listen for global stop events (when navigating away)
  useEffect(() => {
    const handleStopAll = () => {
      if (playing) stopAudio();
    };
    window.addEventListener('music-stop-all', handleStopAll);
    return () => window.removeEventListener('music-stop-all', handleStopAll);
  }, [playing, stopAudio]);

  const togglePlay = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!audioUrl) return;

    if (playing) {
      stopAudio();
      return;
    }

    // Stop any currently playing audio globally
    stopGlobalAudio();

    const audio = new Audio(audioUrl);
    audio.volume = muted ? 0 : 0.5;
    audio.muted = muted;
    globalAudio = audio;
    globalAudioId = idRef.current;

    audio.play().then(() => {
      setPlaying(true);
      setProgress(0);

      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        const pct = Math.min((elapsed / PREVIEW_DURATION) * 100, 100);
        setProgress(pct);
      }, 100);

      timeoutRef.current = setTimeout(() => {
        stopAudio();
      }, PREVIEW_DURATION * 1000);
    }).catch(() => {});

    audio.onended = () => stopAudio();
    audio.onerror = () => stopAudio();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const newMuted = !muted;
    setMuted(newMuted);
    if (globalAudio && globalAudioId === idRef.current) {
      globalAudio.muted = newMuted;
      globalAudio.volume = newMuted ? 0 : 0.5;
    }
  };

  return (
    <div
      onClick={() => onSelect && onSelect()}
      className={`w-full p-4 rounded-xl flex items-center gap-4 border-2 transition-all cursor-pointer
        ${isSelected
          ? 'border-pink-500 bg-pink-50 shadow-md'
          : 'border-transparent bg-white shadow-sm hover:shadow-md hover:border-pink-200'}`}
      role="button"
      tabIndex={0}
    >
      <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 relative">
        <span className="text-2xl">{emoji || '🎵'}</span>
        {audioUrl && (
          <button
            onClick={togglePlay}
            className="absolute -bottom-1 -right-1 w-7 h-7 bg-white rounded-full shadow-md flex items-center justify-center border border-gray-200 z-10"
          >
            {playing ? (
              <div className="flex gap-0.5">
                <div className="w-0.5 h-2.5 bg-pink-500 rounded-full animate-pulse" />
                <div className="w-0.5 h-2.5 bg-pink-500 rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} />
                <div className="w-0.5 h-2.5 bg-pink-500 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
              </div>
            ) : (
              <SafeIcon name="Play" className="text-pink-500 text-xs ml-0.5" />
            )}
          </button>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-gray-800 text-sm">{title}</p>
          {playing && (
            <button
              onClick={toggleMute}
              className="text-gray-400 hover:text-pink-500 transition-colors"
              title={muted ? 'Unmute' : 'Mute'}
            >
              <SafeIcon name={muted ? 'VolumeX' : 'Volume2'} className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400">{artist || 'DayClap'}</p>
        {playing && (
          <div className="mt-1.5 w-full bg-gray-200 rounded-full h-1 overflow-hidden">
            <div
              className="bg-pink-500 h-full rounded-full transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {playing && (
          <p className="text-[10px] text-pink-400 mt-0.5">Preview · {PREVIEW_DURATION}s</p>
        )}
      </div>

      <div className="flex-shrink-0">
        {isPremium ? (
          <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded-full">PRO</span>
        ) : isSelected ? (
          <SafeIcon name="Check" className="text-pink-500" />
        ) : (
          <div className="w-5 h-5 rounded-full border-2 border-gray-200" />
        )}
      </div>
    </div>
  );
};

// Export stop function for use by parent components
export const stopAllMusic = () => {
  stopGlobalAudio();
  window.dispatchEvent(new Event('music-stop-all'));
};

export default MusicPlayer;